import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { SearchResult, SearchState, SearchFilters } from '@/types/search'
import { searchService } from '@/services/search.service'

interface SearchContextValue extends SearchState {
  search: (query: string, filters?: SearchFilters) => Promise<void>
  clearSearch: () => void
  getSuggestions: (query: string) => Promise<SearchResult[]>
}

const SearchContext = createContext<SearchContextValue | undefined>(undefined)

interface SearchProviderProps {
  children: ReactNode
}

export function SearchProvider({ children }: SearchProviderProps) {
  const [searchState, setSearchState] = useState<SearchState>({
    query: '',
    results: [],
    loading: false,
  })

  const search = useCallback(async (query: string, filters?: SearchFilters) => {
    setSearchState((prev) => ({ ...prev, loading: true, query }))

    try {
      const results = await searchService.search(query, filters)
      setSearchState((prev) => ({
        ...prev,
        results,
        loading: false,
        error: undefined,
      }))
    } catch (error) {
      setSearchState((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Search failed',
      }))
    }
  }, [])

  const getSuggestions = useCallback(async (query: string): Promise<SearchResult[]> => {
    if (!query.trim()) return []
    setSearchState((prev) => ({ ...prev, loading: true, query }))

    try {
      const results = await searchService.getSuggestions(query)
      setSearchState((prev) => ({
        ...prev,
        results,
        loading: false,
        error: undefined,
      }))
      return results
    } catch (error) {
      setSearchState((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Search failed',
      }))
      return []
    }
  }, [])

  const clearSearch = useCallback(() => {
    setSearchState({
      query: '',
      results: [],
      loading: false,
      error: undefined,
    })
  }, [])

  const value: SearchContextValue = {
    ...searchState,
    search,
    clearSearch,
    getSuggestions,
  }

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSearch() {
  const context = useContext(SearchContext)
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider')
  }
  return context
}
